import { useState, useEffect, useCallback, useRef, type MouseEvent as ReactMouseEvent } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import type { Locale as DateFnsLocale } from 'date-fns'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
  Video,
  X,
  MessageSquare,
  FileText,
  Pencil,
  type LucideIcon,
} from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ShimmerButton } from '@/components/ui/shimmer-button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { isValidMeetingUrl, getMeetingPlatform, type MeetingLanguage } from '@hub/contracts'
import { orpc } from '@/lib/orpc-client'
import { useProjectEvent } from '@/lib/collab/presence-context'
import { useProject } from '@/lib/project-context'
import { CreateMeetingDialog } from './create-meeting-dialog'
import { StartMeetingDialog } from './start-meeting-dialog'
import {
  formatSelectionRange,
  selectionFromDrag,
  selectionRect,
  type GridSelection,
} from './week-grid-selection'
import { useDateLocale } from '@/i18n/dates'

type ViewMode = 'week' | 'month'
type QuickInviteResult = 'alreadyActive' | 'joining' | 'error'

// ---------------------------------------------------------------------------
// Unified calendar event model
// ---------------------------------------------------------------------------

type CalendarEventType = 'meeting' | 'chat' | 'document' | 'whiteboard'

interface CalendarEvent {
  id: string
  type: CalendarEventType
  title: string
  startAt: string
  endAt: string
  /** Meeting-only fields */
  meetingUrl?: string | null
  cancelledAt?: string | null
  /** PLAN-085 — a HUB video meeting and the state of its room. */
  kind?: 'hub' | 'external'
  roomCode?: string | null
  roomStatus?: 'scheduled' | 'live' | 'ended' | null
}

interface PositionedEvent extends CalendarEvent {
  column: number
  totalColumns: number
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const HOUR_HEIGHT = 64
const SCROLL_TO_HOUR = 7
const ACTIVITY_DURATION_MS = 30 * 60 * 1000

const MEETING_COLOR_CANCELLED =
  'bg-red-100 dark:bg-red-900/40 border-red-300 dark:border-red-700 text-red-900 dark:text-red-100'
const MEETING_COLOR_PASSED =
  'bg-zinc-100 dark:bg-zinc-800/40 border-zinc-300 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400'
const MEETING_COLOR_UPCOMING =
  'bg-blue-100 dark:bg-blue-900/40 border-blue-300 dark:border-blue-700 text-blue-900 dark:text-blue-100'
/** A HUB meeting that is running right now — impossible to miss on purpose. */
const MEETING_COLOR_LIVE =
  'bg-emerald-100 dark:bg-emerald-900/50 border-emerald-500 dark:border-emerald-500 text-emerald-950 dark:text-emerald-50 ring-1 ring-emerald-500'

const ACTIVITY_COLORS: Record<string, string> = {
  chat: 'bg-violet-50 dark:bg-violet-900/30 border-violet-300 dark:border-violet-700 text-violet-900 dark:text-violet-100',
  document:
    'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700 text-emerald-900 dark:text-emerald-100',
  whiteboard:
    'bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-100',
}

const EVENT_ICONS: Record<CalendarEventType, LucideIcon> = {
  meeting: Video,
  chat: MessageSquare,
  document: FileText,
  whiteboard: Pencil,
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getWeekStart(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  d.setDate(diff)
  d.setHours(0, 0, 0, 0)
  return d
}

function getMonthDays(year: number, month: number): Date[] {
  const firstDay = new Date(year, month, 1)
  const startDay = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1
  const days: Date[] = []
  const start = new Date(year, month, 1 - startDay)
  for (let i = 0; i < 42; i++) {
    days.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i))
  }
  return days
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function parseHour(isoString: string): number {
  const d = new Date(isoString)
  return d.getHours() + d.getMinutes() / 60
}

function formatTime(isoString: string): string {
  const d = new Date(isoString)
  return d.getHours().toString().padStart(2, '0') + ':' + d.getMinutes().toString().padStart(2, '0')
}

/** Locale-aware `format` with a capitalised first letter (sv month/day names are lowercase). */
function formatCapitalized(date: Date, pattern: string, locale: DateFnsLocale): string {
  const s = format(date, pattern, { locale })
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function getEventColor(event: CalendarEvent): string {
  if (event.type === 'meeting' && event.roomStatus === 'live') return MEETING_COLOR_LIVE
  if (event.type !== 'meeting') {
    return ACTIVITY_COLORS[event.type] ?? ''
  }
  if (event.cancelledAt) return MEETING_COLOR_CANCELLED
  if (new Date(event.endAt) < new Date()) return MEETING_COLOR_PASSED
  return MEETING_COLOR_UPCOMING
}

function getEventTooltip(event: CalendarEvent, typeLabel: string): string {
  const time =
    event.type === 'meeting'
      ? `${formatTime(event.startAt)} – ${formatTime(event.endAt)}`
      : formatTime(event.startAt)
  return `${typeLabel}: ${event.title}\n${time}`
}

function getDateRange(
  viewMode: ViewMode,
  currentDate: Date
): { from: string; to: string; weekStart: Date } {
  if (viewMode === 'week') {
    const weekStart = getWeekStart(currentDate)
    const weekEnd = new Date(weekStart)
    weekEnd.setDate(weekEnd.getDate() + 7)
    return { from: weekStart.toISOString(), to: weekEnd.toISOString(), weekStart }
  }
  const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 7)
  const gridStart = getMonthDays(currentDate.getFullYear(), currentDate.getMonth())[0]
  return {
    from: gridStart.toISOString(),
    to: monthEnd.toISOString(),
    weekStart: getWeekStart(currentDate),
  }
}

// ---------------------------------------------------------------------------
// Overlap resolver — assigns column positions for concurrent meetings
// ---------------------------------------------------------------------------

/**
 * Greedy column assignment per *overlap cluster*: a pair of concurrent
 * meetings at 09:00 splits only that cluster in two, not every meeting of
 * the day. Activities are not part of this — they are grouped by hour.
 */
function resolveOverlaps(events: CalendarEvent[]): PositionedEvent[] {
  if (events.length === 0) return []
  const sorted = [...events].sort(
    (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
  )
  const positioned: PositionedEvent[] = []
  let cluster: PositionedEvent[] = []
  let columns: number[] = [] // end time per column in the current cluster
  let clusterEnd = -Infinity
  const closeCluster = () => {
    for (const p of cluster) p.totalColumns = columns.length
    cluster = []
    columns = []
  }
  for (const event of sorted) {
    const start = new Date(event.startAt).getTime()
    const end = Math.max(new Date(event.endAt).getTime(), start + 1)
    if (start >= clusterEnd) closeCluster()
    let col = columns.findIndex((colEnd) => colEnd <= start)
    if (col === -1) {
      col = columns.length
      columns.push(end)
    } else {
      columns[col] = end
    }
    clusterEnd = Math.max(clusterEnd, end)
    const p: PositionedEvent = { ...event, column: col, totalColumns: 1 }
    cluster.push(p)
    positioned.push(p)
  }
  closeCluster()
  return positioned
}

interface ActivityGroup {
  hour: number
  items: CalendarEvent[]
}

/** Activities (chat/document/whiteboard) of one day, bucketed per hour. */
function groupActivitiesByHour(events: CalendarEvent[]): ActivityGroup[] {
  const byHour = new Map<number, CalendarEvent[]>()
  for (const event of events) {
    const hour = Math.floor(parseHour(event.startAt))
    const list = byHour.get(hour) ?? []
    list.push(event)
    byHour.set(hour, list)
  }
  return [...byHour.entries()]
    .sort(([a], [b]) => a - b)
    .map(([hour, items]) => ({
      hour,
      items: items.sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()),
    }))
}

/** Hours a meeting covers (for sharing the column with an activity group). */
function meetingHours(event: CalendarEvent): number[] {
  const start = Math.floor(parseHour(event.startAt))
  const end = Math.ceil(parseHour(event.endAt))
  const hours: number[] = []
  for (let h = start; h < Math.max(end, start + 1); h++) hours.push(h)
  return hours
}

const ACTIVITY_ICON_COLORS: Record<string, string> = {
  chat: 'text-violet-600 dark:text-violet-300',
  document: 'text-emerald-600 dark:text-emerald-300',
  whiteboard: 'text-amber-600 dark:text-amber-300',
}

/** Rows shown inside an hour card before it collapses into "+N". */
const GROUP_PREVIEW_ROWS = 2

// ---------------------------------------------------------------------------
// Activity hour card — every chat/document/whiteboard activity of one hour
// in one card (a single row navigates; several open a list).
// ---------------------------------------------------------------------------

function ActivityRow({
  event,
  onOpen,
  className,
}: {
  event: CalendarEvent
  onOpen: (event: CalendarEvent) => void
  className?: string
}) {
  const Icon = EVENT_ICONS[event.type]
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        onOpen(event)
      }}
      className={cn(
        'flex w-full items-center gap-1.5 rounded px-1 text-left hover:bg-accent',
        className
      )}
    >
      <Icon className={cn('h-3 w-3 flex-shrink-0', ACTIVITY_ICON_COLORS[event.type])} />
      <span className="tabular-nums text-muted-foreground flex-shrink-0">
        {formatTime(event.startAt)}
      </span>
      <span className="truncate">{event.title}</span>
    </button>
  )
}

function ActivityHourCard({
  group,
  besideMeeting,
  onOpen,
  typeLabel,
}: {
  group: ActivityGroup
  besideMeeting: boolean
  onOpen: (event: CalendarEvent) => void
  typeLabel: (type: CalendarEventType) => string
}) {
  const { t } = useTranslation('meetings')
  const style = {
    top: `${group.hour * HOUR_HEIGHT + 2}px`,
    height: `${HOUR_HEIGHT - 4}px`,
    left: besideMeeting ? 'calc(55% + 3px)' : '3px',
    right: '3px',
  }
  const hidden = group.items.length - GROUP_PREVIEW_ROWS
  const hourLabel = `${String(group.hour).padStart(2, '0')}:00`

  if (group.items.length === 1) {
    const event = group.items[0]
    const Icon = EVENT_ICONS[event.type]
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            onClick={() => onOpen(event)}
            className={cn(
              'absolute rounded-md border border-dashed px-2 py-1 overflow-hidden cursor-pointer opacity-90 hover:opacity-100',
              getEventColor(event)
            )}
            style={style}
          >
            <p className="text-xs font-medium truncate flex items-center gap-1">
              <Icon className="h-3 w-3 flex-shrink-0" />
              {event.title}
            </p>
            <p className="text-[10px] opacity-70">{formatTime(event.startAt)}</p>
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="whitespace-pre-line text-xs">
          {getEventTooltip(event, typeLabel(event.type))}
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div
          className="absolute rounded-md border border-dashed border-border bg-card/95 px-1 py-1 overflow-hidden cursor-pointer text-[10px] leading-4 hover:bg-accent/60"
          style={style}
        >
          {group.items.slice(0, GROUP_PREVIEW_ROWS).map((event) => (
            <ActivityRow key={`${event.type}-${event.id}`} event={event} onOpen={onOpen} />
          ))}
          {hidden > 0 && (
            <p className="px-1 text-muted-foreground">{t('calendar.more', { count: hidden })}</p>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent side="right" align="start" className="w-72 p-2">
        <p className="mb-1 px-1 text-xs font-medium text-muted-foreground">
          {t('calendar.activitiesAt', { count: group.items.length, hour: hourLabel })}
        </p>
        <div className="flex flex-col gap-0.5 text-xs">
          {group.items.map((event) => (
            <ActivityRow
              key={`${event.type}-${event.id}`}
              event={event}
              onOpen={onOpen}
              className="py-1"
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CalendarView({ projectId }: { projectId: string }) {
  const navigate = useNavigate()
  const { t } = useTranslation('meetings')
  const { dateFns } = useDateLocale()
  const { project } = useProject()
  const [viewMode, setViewMode] = useState<ViewMode>('week')
  const [currentDate, setCurrentDate] = useState(new Date())
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [showActivity, setShowActivity] = useState(true)
  const [meetingsFeature, setMeetingsFeature] = useState(false)
  const [quickInviteOpen, setQuickInviteOpen] = useState(false)
  const [quickInviteUrl, setQuickInviteUrl] = useState('')
  const [quickInviteLoading, setQuickInviteLoading] = useState(false)
  const [quickInviteResult, setQuickInviteResult] = useState<QuickInviteResult | null>(null)
  const [now, setNow] = useState(new Date())
  // PLAN-085 — drag-select in the week grid, and the dialog it opens.
  const [dragging, setDragging] = useState<{ dayIndex: number; anchorY: number } | null>(null)
  const [selection, setSelection] = useState<GridSelection | null>(null)
  const [selectionDay, setSelectionDay] = useState<Date | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [startingMeeting, setStartingMeeting] = useState(false)
  const [startOpen, setStartOpen] = useState(false)
  const [meetingError, setMeetingError] = useState<string | null>(null)
  const [inviteFailures, setInviteFailures] = useState<string[]>([])
  const weekScrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(id)
  }, [])

  const fetchEvents = useCallback(async () => {
    const { from, to } = getDateRange(viewMode, currentDate)

    const [meetingsResult, activityResult] = await Promise.all([
      orpc.meetings.list({ projectId, from, to }),
      orpc.activity.list({ projectId, from, to }),
    ])

    const meetingEvents: CalendarEvent[] = meetingsResult.items.map((m) => ({
      id: m.id,
      type: 'meeting' as const,
      title: m.title,
      startAt: m.startAt,
      endAt: m.endAt,
      meetingUrl: m.meetingUrl,
      cancelledAt: m.cancelledAt,
      kind: m.kind,
      roomCode: m.roomCode,
      roomStatus: m.roomStatus,
    }))

    const activityEvents: CalendarEvent[] = activityResult.items.map((item) => {
      const start = new Date(item.timestamp)
      const end = new Date(start.getTime() + ACTIVITY_DURATION_MS)
      return {
        id: item.id,
        type: item.type as CalendarEventType,
        title: item.title,
        startAt: start.toISOString(),
        endAt: end.toISOString(),
      }
    })

    setEvents([...meetingEvents, ...activityEvents])
    setLoading(false)
  }, [projectId, viewMode, currentDate])

  useEffect(() => {
    fetchEvents()
    const id = setInterval(fetchEvents, 5 * 60_000)
    return () => clearInterval(id)
  }, [fetchEvents])

  // Someone else created/changed a meeting (or an invite email arrived).
  useProjectEvent(['meetings:changed', 'meeting:changed', 'meeting:live'], () => {
    void fetchEvents()
  })

  useEffect(() => {
    if (viewMode === 'week' && weekScrollRef.current) {
      weekScrollRef.current.scrollTop = SCROLL_TO_HOUR * HOUR_HEIGHT
    }
  }, [viewMode, loading])

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  function handleEventClick(event: CalendarEvent) {
    switch (event.type) {
      case 'meeting':
        // A live HUB meeting goes straight into the room; everything else
        // opens the detail page as before.
        if (event.kind === 'hub' && event.roomStatus === 'live' && event.roomCode) {
          navigate({
            to: '/$projectId/meet/$code',
            params: { projectId, code: event.roomCode },
          })
          break
        }
        navigate({
          to: '/$projectId/calendar/$meetingId',
          params: { projectId, meetingId: event.id },
        })
        break
      case 'chat':
        navigate({
          to: '/$projectId/chat/$chatId',
          params: { projectId, chatId: event.id },
        })
        break
      case 'document':
        navigate({
          to: '/$projectId/write/$docId',
          params: { projectId, docId: event.id },
        })
        break
      case 'whiteboard':
        navigate({
          to: '/$projectId/draw/$drawingId',
          params: { projectId, drawingId: event.id },
        })
        break
    }
  }

  // ---------------------------------------------------------------------------
  // HUB video meetings (PLAN-085)
  // ---------------------------------------------------------------------------

  // The flag can be on while the account is not wired up; the button is
  // hidden in that case rather than failing on click.
  useEffect(() => {
    if (!project.features.realtimeMeetings) return
    fetch('/health')
      .then((res) => res.json())
      .then((data: { features?: { meetings?: boolean } }) =>
        setMeetingsFeature(data.features?.meetings === true)
      )
      .catch(() => setMeetingsFeature(false))
  }, [project.features.realtimeMeetings])

  const showMeetingButton = project.features.realtimeMeetings && meetingsFeature

  const handleStartInstant = async (language: MeetingLanguage) => {
    setStartingMeeting(true)
    setMeetingError(null)
    try {
      const meeting = await orpc.meetings.startInstant({ projectId, language })
      if (meeting.roomCode) {
        navigate({
          to: '/$projectId/meet/$code',
          params: { projectId, code: meeting.roomCode },
        })
        return
      }
      setMeetingError(t('hub.startFailed'))
    } catch (err) {
      const reason = (err as { data?: { reason?: string } })?.data?.reason
      setMeetingError(
        reason === 'CREDITS_EXHAUSTED'
          ? t('hub.outOfCredits')
          : reason === 'NOT_CONFIGURED'
            ? t('hub.notConfigured')
            : t('hub.startFailed')
      )
    } finally {
      setStartingMeeting(false)
    }
  }

  const beginDrag = (dayIndex: number, event: ReactMouseEvent<HTMLDivElement>) => {
    // Clicking an existing event must not start a selection.
    if (event.button !== 0 || event.target !== event.currentTarget) return
    const anchorY = event.nativeEvent.offsetY
    setDragging({ dayIndex, anchorY })
    setSelection(selectionFromDrag(dayIndex, anchorY, anchorY, HOUR_HEIGHT))
    setSelectionDay(weekDays[dayIndex] ?? null)
  }

  const extendDrag = (dayIndex: number, event: ReactMouseEvent<HTMLDivElement>) => {
    if (!dragging || dragging.dayIndex !== dayIndex) return
    setSelection(
      selectionFromDrag(dayIndex, dragging.anchorY, event.nativeEvent.offsetY, HOUR_HEIGHT)
    )
  }

  const endDrag = () => {
    if (!dragging) return
    setDragging(null)
    if (selection) setCreateOpen(true)
  }

  // ---------------------------------------------------------------------------
  // Quick invite HUB AI
  // ---------------------------------------------------------------------------

  const handleQuickInvite = async () => {
    if (!isValidMeetingUrl(quickInviteUrl)) return
    setQuickInviteLoading(true)
    setQuickInviteResult(null)
    try {
      const result = await orpc.meetings.quickInvite({ projectId, meetingUrl: quickInviteUrl })
      if (result.status === 'already_active') {
        setQuickInviteResult('alreadyActive')
      } else {
        setQuickInviteResult('joining')
        setTimeout(() => {
          setQuickInviteOpen(false)
          setQuickInviteUrl('')
          setQuickInviteResult(null)
          fetchEvents()
        }, 2000)
      }
    } catch {
      setQuickInviteResult('error')
    } finally {
      setQuickInviteLoading(false)
    }
  }

  // ---------------------------------------------------------------------------
  // Date navigation
  // ---------------------------------------------------------------------------

  const goBack = () => {
    const d = new Date(currentDate)
    if (viewMode === 'week') d.setDate(d.getDate() - 7)
    else d.setMonth(d.getMonth() - 1)
    setCurrentDate(d)
  }

  const goForward = () => {
    const d = new Date(currentDate)
    if (viewMode === 'week') d.setDate(d.getDate() + 7)
    else d.setMonth(d.getMonth() + 1)
    setCurrentDate(d)
  }

  const goToday = () => setCurrentDate(new Date())

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const { weekStart } = getDateRange(viewMode, currentDate)
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart)
    d.setDate(d.getDate() + i)
    return d
  })

  const visibleEvents = showActivity ? events : events.filter((e) => e.type === 'meeting')

  const getEventsForDay = (date: Date) =>
    visibleEvents.filter((e) => isSameDay(new Date(e.startAt), date))

  const dayNames = weekDays.map((d) => formatCapitalized(d, 'EEE', dateFns))
  const eventTypeLabel = (type: CalendarEventType) => t(`eventType.${type}`)

  const getHeaderLabel = () => {
    if (viewMode === 'week') {
      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekEnd.getDate() + 6)
      if (weekStart.getMonth() === weekEnd.getMonth()) {
        return `${weekStart.getDate()} - ${weekEnd.getDate()} ${formatCapitalized(weekStart, 'LLLL yyyy', dateFns)}`
      }
      return `${formatCapitalized(weekStart, 'd LLLL', dateFns)} - ${formatCapitalized(weekEnd, 'd LLLL yyyy', dateFns)}`
    }
    return formatCapitalized(currentDate, 'LLLL yyyy', dateFns)
  }

  const today = new Date()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        {t('calendar.loading')}
      </div>
    )
  }

  return (
    <>
      <TooltipProvider delayDuration={300}>
        <div className="flex flex-col h-full">
          {/* Calendar controls */}
          <div className="flex items-center justify-between px-6 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={goToday} className="bg-transparent">
                {t('calendar.today')}
              </Button>
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={goBack}
                  aria-label={t('calendar.previous')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={goForward}
                  aria-label={t('calendar.next')}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <span className="text-sm font-medium text-foreground ml-2">{getHeaderLabel()}</span>
            </div>
            <div className="flex items-center gap-2">
              {/* PLAN-085: start a HUB video meeting right now. */}
              {showMeetingButton && (
                <Button
                  size="sm"
                  className="h-8 gap-1.5 px-3 text-xs"
                  disabled={startingMeeting}
                  onClick={() => {
                    setMeetingError(null)
                    setStartOpen(true)
                  }}
                >
                  {startingMeeting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Video className="h-3.5 w-3.5" />
                  )}
                  {startingMeeting ? t('hub.starting') : t('hub.start')}
                </Button>
              )}
              {/* Quick invite + Create meeting buttons */}
              <ShimmerButton
                onClick={() => {
                  setQuickInviteUrl('')
                  setQuickInviteResult(null)
                  setQuickInviteOpen(true)
                }}
                className="h-8 px-3 text-xs gap-1.5"
              >
                <Video className="h-3.5 w-3.5" />
                {t('calendar.inviteHub')}
              </ShimmerButton>
              {/* Activity toggle */}
              <button
                onClick={() => setShowActivity(!showActivity)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors',
                  showActivity
                    ? 'bg-violet-100 dark:bg-violet-900/40 border-violet-300 dark:border-violet-700 text-violet-900 dark:text-violet-100'
                    : 'bg-transparent border-border text-muted-foreground hover:text-foreground'
                )}
              >
                {t('calendar.activity')}
              </button>
              {/* View mode switcher */}
              <div className="flex items-center rounded-lg border border-border overflow-hidden">
                <button
                  onClick={() => setViewMode('week')}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium transition-colors',
                    viewMode === 'week'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  {t('calendar.week')}
                </button>
                <button
                  onClick={() => setViewMode('month')}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium transition-colors',
                    viewMode === 'month'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  {t('calendar.month')}
                </button>
              </div>
            </div>
          </div>

          {((meetingError && !startOpen) || inviteFailures.length > 0) && (
            <div className="border-border border-b px-6 py-2">
              {meetingError && !startOpen && (
                <p className="text-destructive text-sm">{meetingError}</p>
              )}
              {inviteFailures.length > 0 && (
                <p className="text-muted-foreground text-sm">
                  {t('create.inviteFailures', { names: inviteFailures.join(', ') })}
                </p>
              )}
            </div>
          )}

          {/* Calendar content */}
          <div
            ref={viewMode === 'week' ? weekScrollRef : undefined}
            className="flex-1 overflow-auto"
          >
            {viewMode === 'week' ? (
              <div className="flex flex-col h-full">
                {/* Sticky day header row */}
                <div className="sticky top-0 z-10 flex bg-background border-b border-border">
                  <div className="w-16 flex-shrink-0 border-r border-border" />
                  <div className="flex-1 grid grid-cols-7">
                    {weekDays.map((day, dayIndex) => {
                      const isToday = isSameDay(day, today)
                      return (
                        <div
                          key={dayIndex}
                          className="h-16 flex flex-col items-center justify-center border-r border-border last:border-r-0"
                        >
                          <span className="text-xs text-muted-foreground">
                            {dayNames[dayIndex]}
                          </span>
                          <span
                            className={cn(
                              'text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full',
                              isToday && 'bg-primary text-primary-foreground'
                            )}
                          >
                            {day.getDate()}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Time grid */}
                <div className="flex flex-1">
                  {/* Time column */}
                  <div className="w-16 flex-shrink-0 border-r border-border">
                    {HOURS.map((hour) => (
                      <div key={hour} className="h-16 relative">
                        <span className="absolute -top-2.5 right-2 text-xs text-muted-foreground">
                          {hour.toString().padStart(2, '0')}:00
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Day columns */}
                  <div className="flex-1 grid grid-cols-7">
                    {weekDays.map((day, dayIndex) => {
                      const dayEvents = getEventsForDay(day)
                      const positioned = resolveOverlaps(
                        dayEvents.filter((e) => e.type === 'meeting')
                      )
                      const activityGroups = groupActivitiesByHour(
                        dayEvents.filter((e) => e.type !== 'meeting')
                      )
                      const activityHours = new Set(activityGroups.map((g) => g.hour))
                      const meetingHourSet = new Set(positioned.flatMap(meetingHours))
                      const isToday = isSameDay(day, today)
                      const isWeekend = dayIndex >= 5

                      const daySelection =
                        selection && selection.dayIndex === dayIndex ? selection : null

                      return (
                        <div
                          key={dayIndex}
                          className={cn(
                            'border-r border-border last:border-r-0 relative',
                            isWeekend && 'bg-muted/30',
                            showMeetingButton && 'cursor-crosshair'
                          )}
                          onMouseDown={(e) => showMeetingButton && beginDrag(dayIndex, e)}
                          onMouseMove={(e) => extendDrag(dayIndex, e)}
                          onMouseUp={endDrag}
                          onMouseLeave={() => dragging?.dayIndex === dayIndex && endDrag()}
                        >
                          {HOURS.map((hour) => (
                            <div
                              key={hour}
                              className="h-16 border-b border-border/50 pointer-events-none"
                            />
                          ))}

                          {/* PLAN-085: the slot being painted right now. */}
                          {daySelection && (
                            <div
                              className="bg-primary/20 border-primary pointer-events-none absolute inset-x-1 z-10 rounded-md border-2"
                              style={selectionRect(daySelection, HOUR_HEIGHT)}
                            >
                              <span className="text-foreground px-1.5 text-[10px] font-medium">
                                {formatSelectionRange(daySelection)}
                              </span>
                            </div>
                          )}

                          {/* Current time indicator */}
                          {isToday &&
                            (() => {
                              const nowHour = now.getHours() + now.getMinutes() / 60
                              const lineTop = nowHour * HOUR_HEIGHT
                              return (
                                <div
                                  className="absolute left-0 right-0 z-20 pointer-events-none"
                                  style={{ top: `${lineTop}px` }}
                                >
                                  <div className="flex items-center">
                                    <div className="w-2 h-2 rounded-full bg-red-500 -ml-1" />
                                    <div className="flex-1 h-[2px] bg-red-500" />
                                  </div>
                                </div>
                              )
                            })()}

                          {/* Meetings */}
                          {positioned.map((event) => {
                            const startHour = parseHour(event.startAt)
                            const endHour = parseHour(event.endAt)
                            const top = startHour * HOUR_HEIGHT
                            const height = Math.max((endHour - startHour) * HOUR_HEIGHT, 24)
                            const Icon = EVENT_ICONS[event.type]
                            // Share the column with an activity card in the same hour.
                            const sharesHour = meetingHours(event).some((h) => activityHours.has(h))
                            const area = sharesHour ? 55 : 100
                            const colWidth = area / event.totalColumns
                            const colLeft = event.column * colWidth

                            return (
                              <Tooltip key={`${event.type}-${event.id}`}>
                                <TooltipTrigger asChild>
                                  <div
                                    onClick={() => handleEventClick(event)}
                                    className={cn(
                                      'absolute rounded-md border px-2 py-1.5 overflow-hidden cursor-pointer transition-opacity hover:opacity-90',
                                      getEventColor(event),
                                      event.cancelledAt && 'opacity-50 line-through'
                                    )}
                                    style={{
                                      top: `${top}px`,
                                      height: `${height}px`,
                                      left: `calc(${colLeft}% + 3px)`,
                                      right: `calc(${100 - colLeft - colWidth}% + 3px)`,
                                    }}
                                  >
                                    <p className="text-xs font-semibold truncate flex items-center gap-1">
                                      <Icon className="h-3 w-3 flex-shrink-0" />
                                      {event.title}
                                    </p>
                                    <p className="text-[10px] opacity-80">
                                      {event.roomStatus === 'live' ? (
                                        <span className="font-semibold">{t('hub.liveBadge')}</span>
                                      ) : (
                                        <>
                                          {formatTime(event.startAt)} - {formatTime(event.endAt)}
                                        </>
                                      )}
                                    </p>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="right"
                                  className="whitespace-pre-line text-xs"
                                >
                                  {getEventTooltip(event, eventTypeLabel(event.type))}
                                </TooltipContent>
                              </Tooltip>
                            )
                          })}

                          {/* Activities: one card per hour */}
                          {activityGroups.map((group) => (
                            <ActivityHourCard
                              key={group.hour}
                              group={group}
                              besideMeeting={meetingHourSet.has(group.hour)}
                              onOpen={handleEventClick}
                              typeLabel={eventTypeLabel}
                            />
                          ))}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            ) : (
              /* Monthly view */
              <div className="h-full flex flex-col">
                {/* Day names header */}
                <div className="grid grid-cols-7 border-b border-border">
                  {dayNames.map((name) => (
                    <div
                      key={name}
                      className="py-2 text-center text-xs font-medium text-muted-foreground border-r border-border last:border-r-0"
                    >
                      {name}
                    </div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="flex-1 grid grid-cols-7 grid-rows-6">
                  {getMonthDays(currentDate.getFullYear(), currentDate.getMonth()).map((day, i) => {
                    const dayEvents = getEventsForDay(day)
                    const sortedDayEvents = [...dayEvents].sort(
                      (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
                    )
                    const isCurrentMonth = day.getMonth() === currentDate.getMonth()
                    const isDayToday = isSameDay(day, today)

                    return (
                      <div
                        key={i}
                        onClick={() => {
                          if (dayEvents.length > 0) {
                            setCurrentDate(day)
                            setViewMode('week')
                          }
                        }}
                        className={cn(
                          'border-r border-b border-border last:border-r-0 p-1.5 min-h-24 flex flex-col',
                          !isCurrentMonth && 'bg-muted/20',
                          dayEvents.length > 0 &&
                            'cursor-pointer hover:bg-muted/40 transition-colors'
                        )}
                      >
                        <span
                          className={cn(
                            'text-xs w-6 h-6 flex items-center justify-center rounded-full self-end',
                            isDayToday && 'bg-primary text-primary-foreground font-bold',
                            !isCurrentMonth && 'text-muted-foreground/50',
                            isCurrentMonth && !isDayToday && 'text-foreground'
                          )}
                        >
                          {day.getDate()}
                        </span>
                        <div className="flex-1 space-y-0.5 mt-1">
                          {sortedDayEvents.slice(0, 3).map((event) => {
                            const isActivity = event.type !== 'meeting'
                            const Icon = EVENT_ICONS[event.type]
                            return (
                              <Tooltip key={`${event.type}-${event.id}`}>
                                <TooltipTrigger asChild>
                                  <div
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleEventClick(event)
                                    }}
                                    className={cn(
                                      'text-[10px] font-medium px-1.5 py-0.5 rounded truncate border cursor-pointer hover:opacity-90 transition-opacity',
                                      isActivity && 'border-dashed',
                                      getEventColor(event),
                                      event.cancelledAt && 'opacity-50 line-through'
                                    )}
                                  >
                                    <Icon className="inline h-2.5 w-2.5 mr-0.5" />
                                    {formatTime(event.startAt)} {event.title}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="bottom"
                                  className="whitespace-pre-line text-xs"
                                >
                                  {getEventTooltip(event, eventTypeLabel(event.type))}
                                </TooltipContent>
                              </Tooltip>
                            )
                          })}
                          {sortedDayEvents.length > 3 && (
                            <span className="text-[10px] text-muted-foreground px-1.5">
                              {t('calendar.more', { count: sortedDayEvents.length - 3 })}
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </TooltipProvider>

      <StartMeetingDialog
        open={startOpen}
        onOpenChange={setStartOpen}
        starting={startingMeeting}
        error={startOpen ? meetingError : null}
        onStart={(language) => void handleStartInstant(language)}
      />

      <CreateMeetingDialog
        projectId={projectId}
        selection={selection}
        day={selectionDay}
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open)
          if (!open) setSelection(null)
        }}
        onCreated={(result) => {
          setInviteFailures(result.inviteFailures)
          setSelection(null)
          void fetchEvents()
        }}
      />

      {/* Quick invite dialog */}
      <Dialog
        open={quickInviteOpen}
        onOpenChange={(open) => {
          setQuickInviteOpen(open)
          if (!open) {
            setQuickInviteUrl('')
            setQuickInviteResult(null)
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5" />
              {t('quickInvite.title')}
            </DialogTitle>
            <p className="text-xs text-muted-foreground">{t('quickInvite.hint')}</p>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label>{t('quickInvite.meetingUrl')}</Label>
              <div className="relative">
                <Input
                  placeholder={t('quickInvite.urlPlaceholder')}
                  value={quickInviteUrl}
                  onChange={(e) => {
                    setQuickInviteUrl(e.target.value)
                    setQuickInviteResult(null)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleQuickInvite()
                    }
                  }}
                  className={cn(
                    quickInviteUrl &&
                      !isValidMeetingUrl(quickInviteUrl) &&
                      'border-red-500 focus-visible:ring-red-500',
                    quickInviteUrl &&
                      isValidMeetingUrl(quickInviteUrl) &&
                      'border-green-500 focus-visible:ring-green-500'
                  )}
                  autoFocus
                />
                {quickInviteUrl && isValidMeetingUrl(quickInviteUrl) && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                )}
              </div>
              {quickInviteUrl && !isValidMeetingUrl(quickInviteUrl) && (
                <p className="text-xs text-red-500">{t('quickInvite.invalidUrl')}</p>
              )}
              {quickInviteUrl && isValidMeetingUrl(quickInviteUrl) && (
                <p className="text-xs text-muted-foreground">
                  {getMeetingPlatform(quickInviteUrl) === 'google_meet'
                    ? t('quickInvite.googleMeet')
                    : t('quickInvite.teams')}
                </p>
              )}
            </div>

            {quickInviteResult && (
              <p
                className={cn(
                  'text-sm',
                  quickInviteResult === 'error' ? 'text-red-500' : 'text-green-600'
                )}
              >
                {t(`quickInvite.${quickInviteResult}`)}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setQuickInviteOpen(false)}>
                {t('quickInvite.cancel')}
              </Button>
              <Button
                onClick={handleQuickInvite}
                disabled={!isValidMeetingUrl(quickInviteUrl) || quickInviteLoading}
              >
                {quickInviteLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('quickInvite.connecting')}
                  </>
                ) : (
                  <>
                    <Video className="h-4 w-4 mr-2" />
                    {t('quickInvite.invite')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
